/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import React, { useMemo, useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Building2, Layers } from "lucide-react";
import { useIsMobile } from "@/hooks/hooks/use-mobile";

// ============================================
// TYPES
// ============================================
export type Milestone = {
	year: string;
	title: string;
	description: string;
};

// ============================================
// DATA
// ============================================
export const milestonesData: Milestone[] = [
	{
		year: "1998",
		title: "The Foundation",
		description:
			"Founded the company, laying the groundwork for a future in specialized engineering and manufacturing.",
	},
	{
		year: "2000",
		title: "Entering the Hydraulic Market",
		description:
			"Commenced manufacturing operations, producing high-quality heat exchangers tailored for the hydraulic sector.",
	},
	{
		year: "2005",
		title: "Major Domestic Approvals",
		description:
			"Achieved significant industry recognition by securing official vendor approvals from power sector giants BHEL and NTPC.",
	},
	{
		year: "2009",
		title: "Global Reach & Innovation",
		description:
			"Expanded into the international market with our first heat exchanger exports to Denmark. Successfully engineered and manufactured our first Double Wall Heat Exchanger.",
	},
	{
		year: "2011",
		title: "Oil & Gas Diversification",
		description:
			"Broadened our industrial footprint by initiating supply operations for the demanding Oil & Gas sector.",
	},
	{
		year: "2012",
		title: "Meeting European Standards",
		description:
			"Demonstrated our commitment to global quality by manufacturing heat exchangers with CE-PED certification.",
	},
	{
		year: "2013",
		title: "Achieving Global Benchmarks",
		description:
			"Upgraded our manufacturing capabilities by securing the highly respected ASME 'U' Stamp and 'NB' Stamp certifications for our facility.",
	},
	{
		year: "2014",
		title: "Super Critical Capabilities",
		description:
			"Secured advanced approval from BHEL for Super Critical Thermal Power Plants, authorizing us to supply for massive projects exceeding 500MW.",
	},
	{
		year: "2018",
		title: "Sustainability & Advanced Certifications",
		description:
			"Entered the Renewable Energy sector. Attained DOSH certification and further elevated our facility standards by earning the ASME 'R' Stamp.",
	},
	{
		year: "2019",
		title: "Partnering with Industry Giants",
		description:
			"Executed our first successful supply to Saudi Aramco, one of the world's leading oil and gas companies.",
	},
	{
		year: "2020",
		title: "Design Innovation",
		description:
			"Launched our highly efficient Compact Heat Exchanger featuring an advanced Continuous Plate Fin Design.",
	},
	{
		year: "2021",
		title: "Environmental Impact & The U2 Stamp",
		description:
			"Achieved the stringent ASME 'U2' Stamp certification. Entered the Wastewater Treatment Sector, delivering solutions to major Metro City WTP projects across India and the Philippines.",
	},
	{
		year: "2022",
		title: "Hydro-Electric Milestones",
		description:
			"Granted specialized application approval by NHPC for Hydro-Electric Power Plants.",
	},
	{
		year: "2023",
		title: "Excellence & High-Temp Solutions",
		description:
			"Honored with 'Best Supplier' awards from global power and oil & gas giants Toshiba T&D and Atlas Copco. Successfully executed a high-temperature flue gas heat recovery project capable of withstanding extreme temperatures above 800°C.",
	},
	{
		year: "2024",
		title: "Global Recognition",
		description:
			"Received the 'Best Supplier Award' from Voith Turbo, the German global leader in power transmission and couplings.",
	},
	{
		year: "2025",
		title: "Mega Manufacturing Facilities",
		description:
			"Executed a prestigious project for Middle Eastern Oil & Gas leader ADNOC. Significantly expanded capacity by launching a new 8,000 Sq. Mtr. manufacturing facility, fully equipped with state-of-the-art automatic welding and advanced machinery.",
	},
	{
		year: "2026",
		title: "New Product Line & EMEA Office Expansion",
		description:
			"A dedicated manufacturing facility for ONAN pressed steel radiators, specializing in transformer oil cooling solutions. Established office in Dubai UAE to serve EMEA clients.",
	},
];

// ============================================
// CONSTANTS (FIXED SYSTEM)
// ============================================
const CARD_HEIGHT = 280;
const SEGMENT_GAP = 220;
const timelinePadding = 90;

// ============================================
// PATH
// ============================================
const snakePath = (steps: number) => {
	const startX = 80;
	const leftX = 30;
	const rightX = 130;

	let d = `M${startX} 40`;
	let currentY = 40;
	let isRight = true;

	for (let i = 0; i < steps; i++) {
		const nextY = currentY + SEGMENT_GAP;

		const cp1x = isRight ? rightX : leftX;
		const cp2x = isRight ? rightX : leftX;

		d += ` C ${cp1x} ${currentY + 80}, ${cp2x} ${nextY - 80}, ${startX} ${nextY}`;

		currentY = nextY;
		isRight = !isRight;
	}

	return d;
};

// ============================================
// ITEM
// ============================================
const TimelineItem = ({
	item,
	index,
	top,
}: {
	item: Milestone;
	index: number;
	top: number;
}) => {
	const isLeft = index % 2 === 0;
	const ref = useRef(null);
	const inView = useInView(ref, { amount: 0.3 });

	return (
		<div
			className="absolute left-0 w-full"
			style={{ top: `${top - CARD_HEIGHT / 2}px` }}>
			<div className="grid items-center md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
				{/* LEFT */}
				{isLeft ?
					<div className="md:pr-12 flex justify-end">
						<motion.div
							ref={ref}
							initial={{ opacity: 0, x: -80 }}
							animate={inView ? { opacity: 1, x: 0 } : {}}
							transition={{ duration: 0.6, ease: [0.25, 0.8, 0.25, 1] }}
							className="w-full max-w-205 h-45 rounded-[28px] bg-white border border-slate-200 shadow-md hover:shadow-xl transition flex">
							<div className="p-6 flex flex-col justify-center h-full">
								<p className="text-xs font-bold tracking-widest text-[#2d2f92]">
									{item.year}
								</p>
								<h3 className="mt-2 text-lg font-semibold">{item.title}</h3>
								<p className="mt-2 text-sm text-slate-600">
									{item.description}
								</p>
							</div>
						</motion.div>
					</div>
				:	<div />}

				{/* CENTER */}
				<div className="relative flex justify-center items-center">
					<div
						className="absolute h-[2px] bg-slate-300"
						style={{
							width: "70px",
							left: isLeft ? "50%" : "auto",
							right: isLeft ? "auto" : "50%",
							transform: isLeft ? "translateX(-100%)" : "translateX(100%)",
						}}
					/>
					<div className="z-10 w-5 h-5 bg-[#f9a93a] rounded-full shadow-lg" />
				</div>

				{/* RIGHT */}
				{!isLeft ?
					<div className="md:pl-12 flex justify-start">
						<motion.div
							ref={ref}
							initial={{ opacity: 0, x: 80 }}
							animate={inView ? { opacity: 1, x: 0 } : {}}
							transition={{ duration: 0.6, ease: [0.25, 0.8, 0.25, 1] }}
							className="w-full max-w-205 h-45 rounded-[28px] bg-white border border-slate-200 shadow-md hover:shadow-xl transition flex">
							<div className="p-6 flex flex-col justify-center h-full">
								<p className="text-xs font-bold tracking-widest text-[#2d2f92]">
									{item.year}
								</p>
								<h3 className="mt-2 text-lg font-semibold">{item.title}</h3>
								<p className="mt-2 text-sm text-slate-600">
									{item.description}
								</p>
							</div>
						</motion.div>
					</div>
				:	<div />}
			</div>
		</div>
	);
};

// ============================================
// MOBILE ITEM
// ============================================
/**
 * The desktop "snake" lays cards out with absolute pixel offsets and a fixed
 * 180px card height — on a phone that clipped long descriptions and left the
 * curve running through the text. Below `md` the milestones render as a plain
 * flowing rail instead, so every card sizes to its own content.
 */
const MobileTimelineItem = ({
	item,
	index,
}: {
	item: Milestone;
	index: number;
}) => {
	const ref = useRef(null);
	const inView = useInView(ref, { amount: 0.25, once: true });

	return (
		<motion.li
			ref={ref}
			initial={{ opacity: 0, y: 24 }}
			animate={inView ? { opacity: 1, y: 0 } : {}}
			transition={{
				duration: 0.55,
				delay: index * 0.05,
				ease: [0.25, 0.8, 0.25, 1],
			}}
			className="relative pl-8">
			{/* Rail dot */}
			<span
				aria-hidden
				className="absolute left-0 top-6 z-10 h-4 w-4 -translate-x-1/2 rounded-full bg-[#f9a93a] shadow-lg ring-4 ring-white"
			/>
			<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-md">
				<p className="text-xs font-bold tracking-widest text-[#2d2f92]">
					{item.year}
				</p>
				<h3 className="mt-2 text-base font-semibold">{item.title}</h3>
				<p className="mt-2 text-sm leading-relaxed text-slate-600">
					{item.description}
				</p>
			</div>
		</motion.li>
	);
};

// ============================================
// MAIN
// ============================================
export default function VerticalTimeline({
	milestones = milestonesData,
}: {
	milestones?: Milestone[];
}) {
	const ref = useRef(null);
	const isMobile = useIsMobile();

	const { scrollYProgress } = useScroll({
		target: ref,
		offset: ["start end", "end start"],
	});

	const pathLength = useTransform(scrollYProgress, [0, 1], [0, 1]);

	const trackPath = useMemo(
		() => snakePath(milestones.length),
		[milestones.length],
	);

	const containerHeight = useMemo(
		() => timelinePadding + milestones.length * SEGMENT_GAP,
		[milestones.length],
	);

	if (isMobile) {
		return (
			<section
				ref={ref}
				className="bg-white py-14">
				<div className="mx-auto max-w-6xl px-4">
					<ol className="relative flex flex-col gap-6 border-l-2 border-slate-200 pl-1">
						{milestones.map((item, i) => (
							<MobileTimelineItem
								key={i}
								item={item}
								index={i}
							/>
						))}
					</ol>
				</div>
			</section>
		);
	}

	return (
		<section
			ref={ref}
			className="py-32 bg-white">
			<div className="max-w-6xl mx-auto px-6">
				<div className="relative">
					{/* PATH */}
					<div
						className="absolute left-1/2 -translate-x-1/2"
						style={{ width: 260, height: containerHeight }}>
						<svg
							viewBox={`0 0 160 ${containerHeight}`}
							className="w-full h-full">
							<path
								d={trackPath}
								stroke="#e2e8f0"
								strokeWidth="14"
								fill="none"
							/>
							<motion.path
								d={trackPath}
								stroke="#f9a93a"
								strokeWidth="14"
								fill="none"
								strokeDasharray="1"
								style={{ pathLength }}
							/>
						</svg>
					</div>

					{/* ITEMS */}
					<div style={{ height: containerHeight }}>
						{milestones.map((item, i) => (
							<TimelineItem
								key={i}
								item={item}
								index={i}
								top={timelinePadding + i * SEGMENT_GAP}
							/>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
